// Contenedor de toasts. Se monta una vez en App.tsx; el store vive
// en ./toastStore.
//
// UX: el usuario SIEMPRE debe poder leer el mensaje con un icono
// que indique el tipo (éxito / error / info). El contenedor se
// posiciona arriba centrado para que sea visible sin tapar el
// contenido principal. Clic en el toast lo cierra.

import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { usePendingOperations } from '@/lib/offline/operations';
import { currentBundle } from '@/lib/offline/bundle';
import { useAuthStore } from '@/stores/auth';

import { useToastStore } from './toastStore';

/** Avisos por incidente, a partir de la cola actual, sin esperar ni modificar el envío. */
function useSyncNotifications() {
  const owner = useAuthStore((state) => state.user?.id ?? null);
  const { operations, connection } = usePendingOperations();
  const farm = currentBundle()?.explotacion_id;
  const scope = `${owner}:${farm}`;
  const episode = useRef({ scope, seen: new Set<string>() });
  useEffect(() => {
    const store = useToastStore.getState();
    if (episode.current.scope !== scope) {
      episode.current = { scope, seen: new Set() };
      store.toasts.forEach((toast) => store.dismiss(toast.id));
    }
    const owned = operations.filter((o) => o.ownerId === owner && o.explotacionId === farm);
    const review = owned.filter((o) => ['conflict', 'error', 'auth'].includes(o.status));
    const retry = owned.filter((o) => o.status === 'retry');
    const offline = owned.length > 0 && connection === 'offline';
    if (!review.length && !retry.length && !offline) {
      store.dismiss('sync');
      if (!owned.length) episode.current.seen.clear();
      return;
    }
    const signals = review.length
      ? review.map((o) => `${o.id}:${o.status}`)
      : retry.length
        ? retry.map((o) => `${o.id}:retry`)
        : ['offline'];
    const message = review.length
      ? 'Hay cambios que necesitan revisión. Tu copia local se conserva.'
      : retry.length
        ? 'Tu cambio está guardado en el móvil. No se ha podido sincronizar.'
        : 'Guardado en este móvil. Se enviará al recuperar conexión.';
    const newProblem = signals.some((signal) => !episode.current.seen.has(signal));
    const visible = store.toasts.find((toast) => toast.key === 'sync');
    if (!newProblem && (!visible || visible.message === message)) return;
    signals.forEach((signal) => episode.current.seen.add(signal));
    store.show({
      key: 'sync',
      kind: review.length ? 'error' : 'info',
      message,
      ttl: 6000,
      action: {
        label: 'Ver pendientes',
        onClick: () => window.dispatchEvent(new Event('offline:open-sync')),
      },
    });
  }, [scope, owner, farm, operations, connection]);
}

export function ToastContainer() {
  useSyncNotifications();
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-2 px-4 pt-4">
      {toasts.map((t) => {
        const Icon = t.kind === 'success' ? CheckCircle2 : t.kind === 'error' ? AlertCircle : Info;
        return (
          <div
            key={t.id}
            role={t.kind === 'error' ? 'alert' : 'status'}
            aria-atomic="true"
            data-testid={`toast-${t.kind}`}
            className={
              'pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-md ' +
              (t.kind === 'success'
                ? 'border-ok-line bg-ok-bg text-ok-text'
                : t.kind === 'error'
                  ? 'border-warning-line bg-warning-bg text-warning-text'
                  : 'border-border bg-surface text-text-primary')
            }
            onClick={() => dismiss(t.id)}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <span>{t.message}</span>
              {t.action && (
                <button
                  type="button"
                  className="mt-1 min-h-12 font-semibold underline"
                  onClick={t.action.onClick}
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              className="flex size-12 shrink-0 items-center justify-center rounded-lg"
              aria-label="Cerrar aviso"
              onClick={() => dismiss(t.id)}
            >
              <X size={18} aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
